import type { ReactNode } from 'react'
import { isApiError } from '../api/client'
import { Icon, type IconName } from './Icon'

export function PageHeader({
  title,
  count,
  description,
  actions,
}: {
  title: string
  count?: number
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="page-header">
      <div>
        <h1 className="page-title">
          {title}
          {count !== undefined && <span className="page-count" aria-label={`${count} total`}>{count}</span>}
        </h1>
        {description && <p className="page-desc">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  )
}

export type BadgeTone = 'neutral' | 'success' | 'error' | 'warning' | 'info'

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function NotAuthorized({ what }: { what: string }) {
  return (
    <div className="state-panel" role="alert" aria-label="Not authorized">
      <Icon name="lock" size={28} />
      <h2>Not authorized</h2>
      <p>
        Your role in this workspace can’t view {what}. Ask a workspace owner or admin for access,
        or switch to another workspace.
      </p>
    </div>
  )
}

export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = isApiError(error)
    ? typeof error.detail === 'string'
      ? error.detail
      : error.title
    : 'Something went wrong.'
  return (
    <div className="state-panel" role="alert">
      <h2>Couldn’t load this page</h2>
      <p>{msg}</p>
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  )
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="state-panel state-panel--quiet" role="status" aria-label={label}>
      <span className="spinner" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  )
}

/**
 * Standard query outcome renderer: loading → 403 not-authorized → other error → content.
 * Pages call this so a role-restricted endpoint becomes a clear panel, not a broken screen.
 */
export function QueryState({
  isLoading,
  error,
  what,
  onRetry,
  children,
}: {
  isLoading: boolean
  error: unknown
  what: string
  onRetry?: () => void
  children: ReactNode
}) {
  if (isLoading) return <Loading />
  if (error) {
    if (isApiError(error) && error.status === 403) return <NotAuthorized what={what} />
    return <ErrorPanel error={error} onRetry={onRetry} />
  }
  return <>{children}</>
}

/**
 * Empty state that previews what the table will look like with ghosted sample rows.
 * The sample rows are aria-hidden and visibly labelled "Example" so they can't be
 * mistaken for real data.
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  sampleHeaders,
  sampleRows,
}: {
  icon: IconName
  title: string
  body: ReactNode
  action?: ReactNode
  sampleHeaders?: string[]
  sampleRows?: ReactNode[][]
}) {
  return (
    <section className="empty" aria-label={title}>
      {sampleHeaders && sampleRows && (
        <div className="empty-sample" aria-hidden="true">
          <span className="empty-sample-tag">Example</span>
          <table className="table">
            <thead>
              <tr>{sampleHeaders.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {sampleRows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="empty-body">
        <span className="empty-icon"><Icon name={icon} size={22} /></span>
        <h2>{title}</h2>
        <p>{body}</p>
        {action}
      </div>
    </section>
  )
}
